import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { flattenSteps, flattenViewSteps, getStepsForLayout } from 'utils/stepDefintion';
import { useStepController } from 'components/Stepper/RouteBasedStepper/StepControllerContext';

const useRouteBasedStepperLayout = ({
    title, productType, stepsBasedOnState, backURL = '', completeURL = '',
}) => {
    const { pathname, search } = useLocation();
    const navigate = useNavigate();

    const viewSteps = useMemo(() => (
        getStepsForLayout(stepsBasedOnState)
    ), [stepsBasedOnState]);

    const flatSteps = useMemo(() => (
        flattenSteps(stepsBasedOnState)
    ), [stepsBasedOnState]);

    const currentStepKey:string = pathname.split('/').pop() as string;
    const currentStepSelector = useMemo(() => (flatSteps[currentStepKey]), [currentStepKey, flatSteps]);

    const currentProcessSelector = useMemo(() => (flatSteps?.[currentStepKey]?.process), [currentStepKey, flatSteps]);

    const currentProcessSteps = useMemo(() => (
        viewSteps[currentProcessSelector]
    ), [currentProcessSelector, viewSteps]);

    const currentProcessStepsFlat = useMemo(() => (flattenViewSteps(currentProcessSteps)), [currentProcessSteps]);
    const currentStepNumber = useMemo(() => (currentProcessStepsFlat.find((item) => (item.label === currentStepKey))?.position ?? 1), [currentStepKey, currentProcessStepsFlat]);

    const contentTitle = useMemo(() => flatSteps[currentStepKey]?.contentTitle, [flatSteps[currentStepKey]?.contentTitle]);
    const contentClassName = useMemo(() => flatSteps[currentStepKey]?.contentClassName, [flatSteps[currentStepKey]?.contentClassName]);

    const {
        handleOnContinueCallback, isStepCompleted, setStepComplete, isContinueDisabled, isContinueLoading, isBackDisabled, pageErrors,
    } = useStepController();

    const stepForwards = useCallback(async () => {
        const nextStep = currentProcessStepsFlat.find((item) => (item.position === currentStepNumber + 1));
        const nextPath = pathname?.split('/').slice(0, -1).join('/')
            .concat(`/${nextStep?.path}`)
            .concat(`${search || ''}`);

        if (currentStepSelector?.isEnd && completeURL) {
            navigate(completeURL);

            return;
        }
        if (!nextStep) {
            return;
        }
        navigate(nextPath as string);
    }, [currentProcessStepsFlat, currentStepNumber, currentStepSelector?.isEnd, completeURL, pathname, search]);

    const stepBack = useCallback(() => {
        const prevStep = currentProcessStepsFlat.find((item) => (item.position === currentStepNumber - 1));
        const nextPath = pathname?.split('/').slice(0, -1).join('/')
            .concat(`/${prevStep?.path}`)
            .concat(`${search || ''}`);

        if (currentStepSelector?.isBegin && backURL) {
            navigate(backURL);

            return;
        }

        if (!prevStep) {
            return;
        }
        navigate(nextPath as string);
    }, [currentProcessStepsFlat, currentStepNumber, currentStepSelector?.isBegin, backURL, pathname, search]);

    const onContinue = useCallback(async () => {
        handleOnContinueCallback();

        if (isStepCompleted) {
            stepForwards();
        }
    }, [handleOnContinueCallback, isStepCompleted, stepForwards]);

    const onBack = useCallback(() => {
        stepBack();
    }, [stepBack]);

    useEffect(() => {
        if (isStepCompleted) {
            stepForwards();
        }

        return () => setStepComplete(false);
    }, [isStepCompleted, stepForwards]);

    return {
        stepper: {
            steps: currentProcessSteps,
            title,
            currentStepNumber,
        },
        contentTitle,
        contentClassName,
        isStepperEnabled: true,
        isFooterSimplified: true,
        onContinue,
        onBack,
        currentStepKey,
        continueDisabled: isContinueDisabled,
        isContinueLoading,
        backDisabled: isBackDisabled,
        hideStepperButtons: false,
        pageErrors,
        productType,
        currentStepSelector,
    };
};

export default useRouteBasedStepperLayout;
